import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AdvertisementAPI } from '../api/AdvertisementAPI';
import schickLogo from '../resources/images/schick-logo-white.png';
import './AdUploadForm.css';

function BarChart({ data, valueKey, labelKey, color = '#e8ede9', secondKey, secondColor = '#00ff88' }) {
  const [hovered, setHovered] = React.useState(null);
  const W = 680, H = 180, PL = 44, PR = 12, PT = 12, PB = 28;
  const chartW = W - PL - PR;
  const chartH = H - PT - PB;
  const maxVal = Math.max(...data.map(d => Math.max(d[valueKey] || 0, secondKey ? (d[secondKey] || 0) : 0)), 1);
  const niceMax = (() => { const mag = Math.pow(10, Math.floor(Math.log10(maxVal))); return [1,2,5,10].map(s => s * mag).find(s => s >= maxVal) || maxVal; })();
  const GRID = 4;
  const barW = chartW / data.length;
  const barPad = Math.max(barW * 0.22, 6);
  const yLabel = (v) => v >= 1000 ? `${(v/1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(v);

  return (
    <div style={{ position:'relative', width:'100%' }}>
      {hovered !== null && (
        <div style={{ position:'absolute', top:0, left: Math.min(PL + hovered * barW + barW / 2, W - 120), transform:'translateX(-50%)', background:'#0c110e', border:'1px solid #2a3330', borderRadius:8, padding:'8px 12px', fontSize:11, color:'#e8ede9', zIndex:10, whiteSpace:'nowrap', pointerEvents:'none', boxShadow:'0 8px 24px rgba(0,0,0,0.5)' }}>
          <div style={{ fontWeight:700, marginBottom:5, color:'#8a9a8e', fontSize:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>{data[hovered][labelKey]}</div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom: secondKey ? 3 : 0 }}>
            <div style={{ width:8, height:8, borderRadius:2, background:color, flexShrink:0 }}/>
            <span style={{ color:'#6a7a6e' }}>{valueKey === 'impressions' ? 'Impressionen' : 'Klicks'}:</span>
            <span style={{ fontWeight:700, color:'#e8ede9' }}>{(data[hovered][valueKey] || 0).toLocaleString('de-AT')}</span>
          </div>
          {secondKey && <div style={{ display:'flex', alignItems:'center', gap:6 }}><div style={{ width:8, height:8, borderRadius:2, background:secondColor, flexShrink:0 }}/><span style={{ color:'#6a7a6e' }}>Klicks:</span><span style={{ fontWeight:700, color:'#e8ede9' }}>{(data[hovered][secondKey] || 0).toLocaleString('de-AT')}</span></div>}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:'auto', display:'block', overflow:'visible' }}>
        {Array.from({ length: GRID + 1 }).map((_, i) => {
          const val = Math.round((niceMax / GRID) * (GRID - i));
          const y = PT + (i / GRID) * chartH;
          return <g key={i}><line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#1e2820" strokeWidth="1" /><text x={PL - 6} y={y + 4} textAnchor="end" fill="#3a4a3e" fontSize="9" fontFamily="system-ui,sans-serif">{yLabel(val)}</text></g>;
        })}
        {data.map((d, i) => {
          const x = PL + i * barW;
          const bh1 = ((d[valueKey] || 0) / niceMax) * chartH;
          const bh2 = secondKey ? ((d[secondKey] || 0) / niceMax) * chartH : 0;
          const isHov = hovered === i;
          return (
            <g key={i} style={{ cursor:'default' }} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
              <rect x={x} y={PT} width={barW} height={chartH} fill="transparent" />
              <rect x={x + barPad} y={PT + chartH - Math.max(bh1, 2)} width={barW - barPad * 2} height={Math.max(bh1, 2)} rx="0" fill={color} opacity={isHov ? 0.2 : 0.12} />
              {secondKey && bh2 > 0 && (
                <rect x={x + barPad} y={PT + chartH - Math.max(bh2, 2)} width={barW - barPad * 2} height={Math.max(bh2, 2)} rx="0" fill={secondColor} opacity={1} filter={isHov ? `drop-shadow(0 0 6px ${secondColor})` : 'none'} />
              )}
              {!secondKey && bh1 > 0 && isHov && <rect x={x + barPad} y={PT + chartH - Math.max(bh1, 2)} width={barW - barPad * 2} height={Math.max(bh1, 2)} rx="0" fill={color} opacity={0.35} filter={`drop-shadow(0 0 8px ${color})`} />}
              <text x={x + barW / 2} y={H - 4} textAnchor="middle" fill={isHov ? '#c8d5cb' : '#3a4a3e'} fontSize="9" fontFamily="system-ui,sans-serif">{d[labelKey]}</text>
            </g>
          );
        })}
        <line x1={PL} y1={PT + chartH} x2={W - PR} y2={PT + chartH} stroke="#2a3330" strokeWidth="1" />
      </svg>
    </div>
  );
}

const DEMO_REPORT = {
  advertisementId: 0,
  displayName: 'Sommer Opening Night',
  companyName: 'Demo GmbH',
  status: 'LIVE',
  generatedAt: new Date().toISOString(),
  period: { startsAt: '2026-06-01T00:00Z', endsAt: '2026-06-30T23:59:59Z' },
  metrics: {
    bookedImpressions: 15000,
    delivered: 8740,
    impressions: 8120,
    clicks: 387,
    ctr: 0.0477,
    averageViewDurationMs: 4120,
    remainingImpressions: 6880,
  },
  breakdowns: {
    byPlatform: [
      { key: 'IOS',     delivered: 5300, impressions: 4960, clicks: 241, ctr: 0.0486, averageViewDurationMs: 4350 },
      { key: 'ANDROID', delivered: 3440, impressions: 3160, clicks: 146, ctr: 0.0462, averageViewDurationMs: 3820 },
    ],
    byLanguage: [
      { key: 'DE', delivered: 6100, impressions: 5690, clicks: 271, ctr: 0.0476, averageViewDurationMs: 4200 },
      { key: 'EN', delivered: 2180, impressions: 2010, clicks:  98, ctr: 0.0488, averageViewDurationMs: 3900 },
      { key: 'TR', delivered:  460, impressions:  420, clicks:  18, ctr: 0.0429, averageViewDurationMs: 3700 },
    ],
    byGender: [
      { key: 'MALE',    delivered: 4560, impressions: 4230, clicks: 187, ctr: 0.0442, averageViewDurationMs: 3980 },
      { key: 'FEMALE',  delivered: 3620, impressions: 3370, clicks: 178, ctr: 0.0528, averageViewDurationMs: 4290 },
      { key: 'DIVERSE', delivered:  560, impressions:  520, clicks:  22, ctr: 0.0423, averageViewDurationMs: 4100 },
    ],
    byAgeGroup: [
      { key: 'AGE_18_24',  delivered: 3100, impressions: 2890, clicks: 155, ctr: 0.0536, averageViewDurationMs: 3750 },
      { key: 'AGE_25_34',  delivered: 2800, impressions: 2620, clicks: 126, ctr: 0.0481, averageViewDurationMs: 4200 },
      { key: 'AGE_35_44',  delivered: 1620, impressions: 1490, clicks:  72, ctr: 0.0483, averageViewDurationMs: 4450 },
      { key: 'AGE_45_54',  delivered:  820, impressions:  760, clicks:  26, ctr: 0.0342, averageViewDurationMs: 4100 },
      { key: 'AGE_55_PLUS',delivered:  400, impressions:  360, clicks:   8, ctr: 0.0222, averageViewDurationMs: 3600 },
    ],
  },
  dailyStats: [
    { date: '2026-06-01T00:00Z', delivered: 180, impressions: 165, clicks:  8, ctr: 0.0485, averageViewDurationMs: 3900 },
    { date: '2026-06-02T00:00Z', delivered: 290, impressions: 270, clicks: 13, ctr: 0.0481, averageViewDurationMs: 4050 },
    { date: '2026-06-03T00:00Z', delivered: 420, impressions: 390, clicks: 19, ctr: 0.0487, averageViewDurationMs: 4100 },
    { date: '2026-06-04T00:00Z', delivered: 510, impressions: 475, clicks: 23, ctr: 0.0484, averageViewDurationMs: 4200 },
    { date: '2026-06-05T00:00Z', delivered: 480, impressions: 445, clicks: 22, ctr: 0.0494, averageViewDurationMs: 4150 },
    { date: '2026-06-06T00:00Z', delivered: 530, impressions: 495, clicks: 24, ctr: 0.0485, averageViewDurationMs: 4300 },
    { date: '2026-06-07T00:00Z', delivered: 610, impressions: 570, clicks: 28, ctr: 0.0491, averageViewDurationMs: 4250 },
    { date: '2026-06-08T00:00Z', delivered: 570, impressions: 530, clicks: 26, ctr: 0.0491, averageViewDurationMs: 4100 },
    { date: '2026-06-09T00:00Z', delivered: 490, impressions: 455, clicks: 22, ctr: 0.0484, averageViewDurationMs: 4050 },
    { date: '2026-06-10T00:00Z', delivered: 440, impressions: 408, clicks: 20, ctr: 0.0490, averageViewDurationMs: 4000 },
    { date: '2026-06-11T00:00Z', delivered: 380, impressions: 352, clicks: 17, ctr: 0.0483, averageViewDurationMs: 3950 },
    { date: '2026-06-12T00:00Z', delivered: 410, impressions: 380, clicks: 18, ctr: 0.0474, averageViewDurationMs: 4100 },
    { date: '2026-06-13T00:00Z', delivered: 460, impressions: 428, clicks: 21, ctr: 0.0491, averageViewDurationMs: 4200 },
    { date: '2026-06-14T00:00Z', delivered: 520, impressions: 485, clicks: 24, ctr: 0.0495, averageViewDurationMs: 4300 },
    { date: '2026-06-15T00:00Z', delivered: 480, impressions: 445, clicks: 21, ctr: 0.0472, averageViewDurationMs: 4150 },
    { date: '2026-06-16T00:00Z', delivered: 350, impressions: 325, clicks: 15, ctr: 0.0462, averageViewDurationMs: 3950 },
    { date: '2026-06-17T00:00Z', delivered: 280, impressions: 258, clicks: 12, ctr: 0.0465, averageViewDurationMs: 3850 },
    { date: '2026-06-18T00:00Z', delivered: 310, impressions: 287, clicks: 14, ctr: 0.0488, averageViewDurationMs: 3900 },
    { date: '2026-06-19T00:00Z', delivered: 360, impressions: 334, clicks: 16, ctr: 0.0479, averageViewDurationMs: 4000 },
    { date: '2026-06-20T00:00Z', delivered: 390, impressions: 361, clicks: 18, ctr: 0.0499, averageViewDurationMs: 4050 },
    { date: '2026-06-21T00:00Z', delivered: 290, impressions: 269, clicks: 13, ctr: 0.0483, averageViewDurationMs: 3900 },
  ],
};

export default function PublicReportPage() {
  const { token } = useParams();
  const { state: navState } = useLocation();
  const isDemo = token === 'demo';
  const [report, setReport] = useState(isDemo ? DEMO_REPORT : null);
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [adStatus, setAdStatus] = useState(null);
  const [chartMetric, setChartMetric] = useState('impressions');
  const [lang, setLang] = useState('de');

  const t = {
    de: {
      reportLabel: 'Performance Report',
      desc: 'Dieser Report gibt Ihnen einen vollständigen Überblick über die Performance Ihrer Werbekampagne in der Schick Vienna App.',
      started: 'Gestartet',
      ends: 'Endet',
      running: 'Läuft bis zur vollständigen Auslieferung',
      preview: 'App-Vorschau',
      learnMore: 'Mehr erfahren',
      delivery: 'Auslieferungsfortschritt',
      delivered: 'ausgeliefert',
      remaining: 'verbleibend',
      chart: 'Tagesverlauf',
      impressions: 'Impressionen',
      clicks: 'Klicks',
      breakdown: 'Zielgruppen-Breakdown',
      segment: 'Segment',
      avgDuration: 'Ø Dauer',
      loading: 'Report wird geladen…',
      errorExpired: 'Dieser Report-Link ist abgelaufen.',
      errorGeneric: 'Report konnte nicht geladen werden.',
      errorContact: 'Bitte wende dich an Schick Vienna.',
      booked: 'gebucht',
      ctr: 'CTR',
      avgSub: 'Durchschnitt pro Ausspielung',
      remainingSub: 'Impressionen offen',
      generatedAt: 'Generiert am',
      pendingBadge: 'In Vorbereitung',
      pendingTitles: [
        'Ihre Kampagne\nwird vorbereitet',
        'Ihre Kampagne\nwird vorbereitet',
        'Ihre Rechnung\nist unterwegs',
        'Zahlung erhalten –\nKampagne startet bald',
      ],
      pendingDescs: [
        'Ihre Werbemittel wurden erfolgreich übermittelt und werden von unserem Team geprüft. Sobald Ihre Kampagne live geschaltet ist, erscheinen hier alle Performance-Daten in Echtzeit.',
        'Ihre Werbemittel wurden erfolgreich übermittelt und werden von unserem Team geprüft. Sobald Ihre Kampagne live geschaltet ist, erscheinen hier alle Performance-Daten in Echtzeit.',
        'Sie haben Ihre Rechnung per E-Mail erhalten. Sobald Ihr Zahlungseingang bei uns verbucht ist, wird Ihre Kampagne automatisch live geschaltet und die Performance-Daten erscheinen hier in Echtzeit.',
        'Vielen Dank – Ihre Zahlung wurde erfolgreich verbucht. Ihre Kampagne wird in Kürze aktiviert. Die Performance-Daten erscheinen hier, sobald die ersten Auslieferungen stattgefunden haben.',
      ],
      pendingNote: 'Dieser Link bleibt dauerhaft aktiv und aktualisiert sich automatisch, sobald Ihre Kampagne gestartet ist.',
      pendingSteps: ['Eingereicht', 'Prüfung', 'Rechnung', 'Live'],
      pendingSkeletonLabel: 'Auswertungen erscheinen hier, sobald Ihre Kampagne live ist.',
      emptyBreakdown: 'Keine Daten vorhanden',
    },
    en: {
      reportLabel: 'Performance Report',
      desc: 'This report gives you a comprehensive overview of your advertising campaign performance in the Schick Vienna App.',
      started: 'Started',
      ends: 'Ends',
      running: 'Runs until full delivery',
      preview: 'App Preview',
      learnMore: 'Learn more',
      delivery: 'Delivery Progress',
      delivered: 'delivered',
      remaining: 'remaining',
      chart: 'Daily Overview',
      impressions: 'Impressions',
      clicks: 'Clicks',
      breakdown: 'Audience Breakdown',
      segment: 'Segment',
      avgDuration: 'Avg. Duration',
      loading: 'Loading report…',
      errorExpired: 'This report link has expired.',
      errorGeneric: 'Report could not be loaded.',
      errorContact: 'Please contact Schick Vienna.',
      booked: 'booked',
      ctr: 'CTR',
      avgSub: 'Average per impression',
      remainingSub: 'Impressions remaining',
      generatedAt: 'Generated on',
      pendingBadge: 'In Preparation',
      pendingTitles: [
        'Your Campaign\nIs Being Prepared',
        'Your Campaign\nIs Being Prepared',
        'Your Invoice\nHas Been Sent',
        'Payment Received –\nCampaign Launching Soon',
      ],
      pendingDescs: [
        'Your advertising materials have been successfully submitted and are being reviewed by our team. Once your campaign goes live, all performance data will appear here in real time.',
        'Your advertising materials have been successfully submitted and are being reviewed by our team. Once your campaign goes live, all performance data will appear here in real time.',
        'You have received your invoice by email. As soon as we confirm receipt of your payment, your campaign will go live automatically and all performance data will appear here in real time.',
        'Thank you – your payment has been successfully processed. Your campaign will be activated shortly. Performance data will appear here as soon as the first impressions have been delivered.',
      ],
      pendingNote: 'This link stays permanently active and updates automatically once your campaign has launched.',
      pendingSteps: ['Submitted', 'Review', 'Invoice', 'Live'],
      pendingSkeletonLabel: 'Analytics will appear here once your campaign is live.',
      emptyBreakdown: 'No data available yet',
    },
  }[lang];

  useEffect(() => {
    if (isDemo) return;
    AdvertisementAPI.getPublicReport(token)
      .catch(() => AdvertisementAPI.getPublicReportBySession(token))
      .then(setReport)
      .catch(e => {
        if (e?.response?.status === 410) setError(t.errorExpired);
        else {
          setPending(true);
          AdvertisementAPI.getPublicAdvertisementStatus(token)
            .then(res => { if (res?.advertisementStatus) setAdStatus(res.advertisementStatus); })
            .catch(() => {});
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const fmtMs  = (ms) => ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`;
  const fmtPct = (v)  => v  != null ? `${(v * 100).toFixed(1)}%` : '—';
  const fmtNum = (v)  => v  != null ? v.toLocaleString('de-AT') : '—';
  const fmtKey = (k)  => {
    const map = { MALE:'Männlich', FEMALE:'Weiblich', DIVERSE:'Divers', OTHER:'Sonstige', UNKNOWN:'Unbekannt', DE:'Deutsch', EN:'Englisch', IOS:'iOS', ANDROID:'Android', UNDER_18:'Unter 18', AGE_18_24:'18–24', AGE_25_34:'25–34', AGE_35_44:'35–44', AGE_45_54:'45–54', AGE_55_PLUS:'55+' };
    return map[k] || k;
  };

  const metrics  = report?.metrics;
  const booked   = metrics?.bookedImpressions ?? null;
  const delivPct = metrics ? Math.min(100, Math.round((metrics.impressions / (booked || 1)) * 100)) : 0;

  const dailyData = (report?.dailyStats || []).map(d => ({
    ...d,
    label: new Date(d.date).toLocaleDateString('de-AT', { day:'2-digit', month:'2-digit' }),
  }));

  const BREAKDOWN_TABS = [
    { key: 'byPlatform', label: 'Plattform',    icon: <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M17 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-5 17a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4-4H8V5h8v10z"/></svg> },
    { key: 'byLanguage', label: 'Sprache',      icon: <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm-1 17.93V18a1 1 0 0 0-2 0v1.93A8.01 8.01 0 0 1 4.07 13H6a1 1 0 0 0 0-2H4.07A8.01 8.01 0 0 1 11 4.07V6a1 1 0 0 0 2 0V4.07A8.01 8.01 0 0 1 19.93 11H18a1 1 0 0 0 0 2h1.93A8.01 8.01 0 0 1 13 19.93V18a1 1 0 0 0-2 0v1.93z"/></svg> },
    { key: 'byGender',   label: 'Geschlecht',   icon: <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M12 2a5 5 0 1 0 0 10A5 5 0 0 0 12 2zm0 12c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z"/></svg> },
    { key: 'byAgeGroup', label: 'Altersgruppe', icon: <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg> },
  ];

  const METRIC_CARDS = metrics ? [
    { label: t.impressions,   value: fmtNum(metrics.impressions),          sub: `${fmtNum(booked)} ${t.booked}`,  accent:'#e8ede9', icon:<svg viewBox="0 0 24 24" width="15" height="15" fill="none"><path d="M12 5C7 5 2.73 8.11 1 12.5 2.73 16.89 7 20 12 20s9.27-3.11 11-7.5C21.27 8.11 17 5 12 5zm0 12.5a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" fill="currentColor"/></svg> },
    { label: t.clicks,        value: fmtNum(metrics.clicks),               sub: `${t.ctr} ${fmtPct(metrics.ctr)}`, accent:'#00ff88', icon:<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> },
    { label: lang === 'de' ? 'Ansichtsdauer' : 'View Duration', value: fmtMs(metrics.averageViewDurationMs), sub: t.avgSub, accent:'#3b82f6', icon:<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm.5-13a1 1 0 0 0-1 1v4.382l-2.276 1.138a1 1 0 1 0 .894 1.788l2.724-1.362A1 1 0 0 0 13.5 13V8a1 1 0 0 0-1-1z"/></svg> },
    { label: lang === 'de' ? 'Verbleibend' : 'Remaining',       value: fmtNum(metrics.remainingImpressions), sub: t.remainingSub, accent:'#f59e0b', icon:<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z"/></svg> },
  ] : [];

  /* ── Pending state (campaign not yet live) ── */
  if (!loading && pending) {
    const STEPS = t.pendingSteps;
    const STATUS_STEP = { DRAFT: 1, INVOICE_SENT: 2, PAID: 3, SCHEDULED: 3, LIVE: 3, PAUSED: 3, ENDED: 3 };
    const activeStepIdx = STATUS_STEP[adStatus] ?? 1;
    const SK = { background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:14 };
    return (
      <div style={{ position:'fixed', inset:0, overflowY:'auto', background:'#0d1410', color:'rgba(210,228,218,0.88)', fontFamily:'-apple-system,BlinkMacSystemFont,"SF Pro Text",Inter,"Segoe UI",Roboto,Helvetica,Arial,sans-serif', zIndex:9999 }}>

        {/* ══ HEADER ══ */}
        <header className="prp-anim-fade prp-d0" style={{ position:'sticky', top:0, zIndex:10, background:'rgba(13,20,16,0.92)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)' }}>
          <div style={{ maxWidth:920, margin:'0 auto', padding:'0 40px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <img src={schickLogo} alt="Schick Vienna" style={{ height:46, width:'auto', mixBlendMode:'screen', opacity:0.88 }}/>
            <div style={{ display:'flex', alignItems:'center', gap:0 }}>
              {[{l:'de',label:'Deutsch'},{l:'en',label:'English'}].map(({l,label}, i) => (
                <React.Fragment key={l}>
                  {i > 0 && <span style={{ width:1, height:13, background:'rgba(255,255,255,0.12)', flexShrink:0 }}/>}
                  <button onClick={() => setLang(l)} style={{ padding:'4px 14px', border:'none', background:'transparent', color: lang===l ? 'rgba(225,240,232,0.92)' : 'rgba(200,218,208,0.38)', fontSize:12, fontWeight: 400, cursor:'pointer', letterSpacing:'0.02em', fontFamily:'inherit', transition:'color 0.18s' }}>
                    {label}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
          {/* Bottom bar */}
          <div style={{ margin:'0 40px 0', height:2, borderRadius:9999, background:'rgba(255,255,255,0.08)' }}/>
        </header>

        {/* ══ HERO ══ */}
        <div style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', padding:'52px 40px 48px', position:'relative' }}>
          <div style={{ maxWidth:920, margin:'0 auto', display:'flex', alignItems:'center', gap:56, position:'relative' }}>

            {/* LEFT */}
            <div style={{ flex:1, minWidth:0 }}>

              {/* Status badge */}
              <div className="prp-anim prp-d1" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 16px', borderRadius:9999, background:'rgba(255,255,255,0.92)', marginBottom:26 }}>
                <svg width="13" height="13" viewBox="0 0 14 14" style={{ animation:'prp-spin 0.85s linear infinite', flexShrink:0, display:'block' }}>
                  <circle cx="7" cy="7" r="5.5" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="1.6"/>
                  <circle cx="7" cy="7" r="5.5" fill="none" stroke="rgba(0,0,0,0.82)" strokeWidth="1.6" strokeLinecap="round" strokeDasharray="11 23.5"/>
                </svg>
                <span style={{ fontSize:12, fontWeight:700, letterSpacing:'0.01em', color:'rgba(0,0,0,0.6)' }}>
                  {STEPS[activeStepIdx]}
                </span>
              </div>

              <h1 className="prp-anim prp-d2" style={{ fontSize:'clamp(24px,3.2vw,34px)', fontWeight:700, lineHeight:1.22, letterSpacing:'-0.02em', color:'rgba(225,238,230,0.95)', margin:'0 0 16px', whiteSpace:'pre-line' }}>
                {t.pendingTitles[activeStepIdx] ?? t.pendingTitles[1]}
              </h1>

              <p className="prp-anim prp-d3" style={{ fontSize:13.5, color:'rgba(210,228,218,0.82)', lineHeight:1.75, margin:'0 0 36px', maxWidth:440 }}>
                {t.pendingDescs[activeStepIdx] ?? t.pendingDescs[1]}
              </p>

              {/* ── Kampagnenstatus stepper ── */}
              <div className="prp-anim prp-d4" style={{ fontSize:9, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(200,218,208,0.5)', marginBottom:16 }}>
                {lang==='de' ? 'Kampagnenstatus' : 'Campaign Status'}
              </div>
              <div className="prp-anim prp-d4" style={{ display:'flex', alignItems:'flex-start', gap:0, maxWidth:380 }}>
                {STEPS.map((label, i) => {
                  const done   = i < activeStepIdx;
                  const active = i === activeStepIdx;
                  const isLast = i === STEPS.length - 1;
                  return (
                    <React.Fragment key={label}>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:9, flexShrink:0, width:64 }}>
                        <div style={{
                          width:30, height:30, borderRadius:'50%',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          background: done ? 'rgba(0,180,100,0.1)' : 'transparent',
                          border: done
                            ? '1.5px solid rgba(0,180,100,0.55)'
                            : active
                            ? '1.5px solid rgba(255,255,255,0.32)'
                            : '1.5px solid rgba(255,255,255,0.15)',
                        }}>
                          {done
                            ? <svg viewBox="0 0 24 24" fill="none" stroke="rgba(0,200,120,0.9)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M20 6L9 17l-5-5"/></svg>
                            : <span style={{ fontSize:10.5, fontWeight:500, color: active ? 'rgba(215,232,222,0.82)' : 'rgba(200,218,208,0.45)', fontVariantNumeric:'tabular-nums' }}>{i+1}</span>
                          }
                        </div>
                        <span style={{ fontSize:10, fontWeight: done ? 600 : active ? 500 : 400, color: done ? 'rgba(0,210,130,0.9)' : active ? 'rgba(215,232,222,0.85)' : 'rgba(200,218,208,0.5)', textAlign:'center', lineHeight:1.3 }}>
                          {label}
                        </span>
                      </div>
                      {!isLast && (
                        <div style={{ flex:1, height:1, marginTop:15, background: done ? 'rgba(0,180,100,0.35)' : 'rgba(255,255,255,0.12)' }}/>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* RIGHT — phone */}
            <div className="auf-phone-wrap prp-anim-right prp-d2" style={{ flexShrink:0 }}>
              <div className="auf-phone-label">{t.preview}</div>
              <div className="auf-phone">
                <div className="auf-phone-frame">
                  <div className="auf-phone-statusbar">
                    <span>9:41</span>
                    <span className="auf-phone-statusbar-icons">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><path d="M1.5 8.5a13 13 0 0 1 21 0M5 12a10 10 0 0 1 14 0M8.5 15.5a6 6 0 0 1 7 0M12 19h.01"/></svg>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><rect x="2" y="7" width="16" height="11" rx="2"/><path d="M22 11v3"/></svg>
                    </span>
                  </div>
                  <div className="auf-phone-ad">
                    {(() => {
                      const img = (lang==='en' && navState?.previewEN) ? navState.previewEN : navState?.previewDE;
                      return img
                        ? <img src={img} alt="" className="auf-phone-ad-img"/>
                        : (
                          <div className="auf-phone-ad-placeholder">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                            <span>Werbebild</span>
                          </div>
                        );
                    })()}
                    <div className="auf-phone-overlay"/>
                    <div className="auf-phone-ad-label">{lang==='en' ? 'Ad' : 'Anzeige'}</div>
                    <button className="auf-phone-close" type="button" tabIndex={-1}>✕</button>
                    <div className="auf-phone-content">
                      {navState?.displayName && <div className="auf-phone-advertiser">{navState.displayName}</div>}
                      {navState?.descriptionDE && <p className="auf-phone-desc">{lang==='en' ? '' : navState.descriptionDE}</p>}
                      <div className="auf-phone-cta">
                        {t.learnMore}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="9" height="9"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                      </div>
                    </div>
                    <div className="auf-phone-home"/>
                  </div>
                </div>
              </div>
              <div className="auf-phone-note">{lang==='de' ? 'So sieht Ihre Anzeige in der App aus' : 'How your ad looks in the app'}</div>
            </div>

          </div>
        </div>

        {/* ══ BODY ══ */}
        <div style={{ maxWidth:920, margin:'0 auto', padding:'40px 40px 88px' }}>

          {/* ── KPI skeleton grid ── */}
          <div className="prp-anim prp-d5" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:10 }}>
            {[
              { w1:'48%', w2:'62%' },
              { w1:'38%', w2:'55%' },
              { w1:'52%', w2:'45%' },
              { w1:'42%', w2:'68%' },
            ].map((sizes, i) => (
              <div key={i} style={{ ...SK, padding:'22px 20px 20px' }}>
                <div className="prp-sk" style={{ width:16, height:16, borderRadius:4, marginBottom:16 }}/>
                <div className="prp-sk" style={{ width:sizes.w1, height:20, borderRadius:5, marginBottom:9 }}/>
                <div className="prp-sk" style={{ width:sizes.w2, height:9, borderRadius:3, marginBottom:6 }}/>
                <div className="prp-sk" style={{ width:'30%', height:9, borderRadius:3 }}/>
              </div>
            ))}
          </div>

          {/* ── Delivery skeleton ── */}
          <div className="prp-anim prp-d6" style={{ ...SK, padding:'24px 28px', marginBottom:10 }}>
            <div className="prp-sk" style={{ width:'14%', height:9, borderRadius:3, marginBottom:14 }}/>
            <div className="prp-sk" style={{ width:'22%', height:28, borderRadius:5, marginBottom:20 }}/>
            <div style={{ height:3, background:'rgba(255,255,255,0.05)', borderRadius:99, marginBottom:12, overflow:'hidden' }}>
              <div className="prp-sk" style={{ width:'100%', height:'100%', borderRadius:99 }}/>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <div className="prp-sk" style={{ width:'16%', height:9, borderRadius:3 }}/>
              <div className="prp-sk" style={{ width:'16%', height:9, borderRadius:3 }}/>
            </div>
          </div>

          {/* ── Chart skeleton ── */}
          <div className="prp-anim prp-d7" style={{ ...SK, padding:'22px 24px 18px', marginBottom:40 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <div className="prp-sk" style={{ width:'14%', height:9, borderRadius:3 }}/>
              <div style={{ display:'flex', gap:16 }}>
                <div className="prp-sk" style={{ width:52, height:9, borderRadius:3 }}/>
                <div className="prp-sk" style={{ width:36, height:9, borderRadius:3 }}/>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'flex-end', gap:5, height:108 }}>
              {[42,58,50,70,54,38,64,47,60,68,44,55].map((h,i) => (
                <div key={i} className="prp-sk" style={{ flex:1, height:`${h}%`, borderRadius:'2px 2px 0 0' }}/>
              ))}
            </div>
            <div style={{ height:1, background:'rgba(255,255,255,0.04)', marginTop:0 }}/>
          </div>

        </div>

        {/* ══ FOOTER ══ */}
        <footer className="prp-anim-fade prp-d7" style={{ background:'#111a15', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ maxWidth:920, margin:'0 auto', padding:'44px 40px 40px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:40 }}>
              <div>
                <p style={{ margin:0, fontSize:12, color:'rgba(200,218,208,0.45)', lineHeight:1.7, maxWidth:320 }}>
                  {t.pendingNote}
                </p>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontSize:10, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(200,218,208,0.3)', marginBottom:10 }}>
                  {lang==='de' ? 'Kontakt' : 'Contact'}
                </div>
                <a href="mailto:office@schick-vienna.at" style={{ fontSize:12, color:'rgba(200,218,208,0.55)', textDecoration:'none', display:'block' }}>
                  office@schick-vienna.at
                </a>
              </div>
            </div>
            <div style={{ marginTop:36, paddingTop:20, borderTop:'1px solid rgba(255,255,255,0.05)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:11, color:'rgba(200,218,208,0.25)' }}>
                © {new Date().getFullYear()} Schick Vienna
              </span>
              <span style={{ fontSize:11, color:'rgba(200,218,208,0.25)' }}>
                {t.pendingSkeletonLabel}
              </span>
            </div>
          </div>
        </footer>

        <style>{`
          @keyframes prp-spin { to { transform: rotate(360deg); } }
          @keyframes prp-fade-up {
            from { opacity: 0; transform: translateY(22px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes prp-fade-in {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          @keyframes prp-slide-right {
            from { opacity: 0; transform: translateX(28px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          .prp-anim       { animation: prp-fade-up    0.65s cubic-bezier(0.22,1,0.36,1) both; }
          .prp-anim-fade  { animation: prp-fade-in    0.5s  ease                        both; }
          .prp-anim-right { animation: prp-slide-right 0.65s cubic-bezier(0.22,1,0.36,1) both; }
          .prp-d0 { animation-delay:   0ms; }
          .prp-d1 { animation-delay:  80ms; }
          .prp-d2 { animation-delay: 160ms; }
          .prp-d3 { animation-delay: 240ms; }
          .prp-d4 { animation-delay: 320ms; }
          .prp-d5 { animation-delay: 400ms; }
          .prp-d6 { animation-delay: 480ms; }
          .prp-d7 { animation-delay: 560ms; }
          @keyframes prp-wave {
            0%   { background-position: -800px 0; }
            100% { background-position:  800px 0; }
          }
          .prp-sk {
            background: linear-gradient(
              90deg,
              rgba(255,255,255,0.055) 0%,
              rgba(255,255,255,0.095) 50%,
              rgba(255,255,255,0.055) 100%
            );
            background-size: 800px 100%;
            animation: prp-wave 2.8s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ position:'fixed', inset:0, overflowY:'auto', background:'#0d1410', color:'rgba(200,221,208,0.9)', fontFamily:'-apple-system,BlinkMacSystemFont,"SF Pro Text",Inter,"Segoe UI",Roboto,Helvetica,Arial,sans-serif', zIndex:9999 }}>

      {/* ══ HEADER ══ */}
      <header className="pr-anim-fade pr-d0" style={{ position:'sticky', top:0, zIndex:10, background:'rgba(13,20,16,0.92)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)' }}>
        <div style={{ maxWidth:920, margin:'0 auto', padding:'0 40px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <img src={schickLogo} alt="Schick Vienna" style={{ height:46, width:'auto', mixBlendMode:'screen', opacity:0.88 }}/>
          <div style={{ display:'flex', alignItems:'center', gap:0 }}>
            {[{l:'de',label:'Deutsch'},{l:'en',label:'English'}].map(({l,label}, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span style={{ width:1, height:13, background:'rgba(255,255,255,0.12)', flexShrink:0 }}/>}
                <button onClick={() => setLang(l)} style={{ padding:'4px 14px', border:'none', background:'transparent', color: lang===l ? 'rgba(225,240,232,0.92)' : 'rgba(200,218,208,0.38)', fontSize:12, fontWeight:400, cursor:'pointer', letterSpacing:'0.02em', fontFamily:'inherit', transition:'color 0.18s' }}>
                  {label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div style={{ margin:'0 40px', height:2, borderRadius:9999, background:'rgba(255,255,255,0.08)' }}/>
      </header>

      {/* Demo banner */}
      {isDemo && (
        <div style={{ background:'rgba(251,191,36,0.08)', borderBottom:'1px solid rgba(251,191,36,0.18)', padding:'10px 40px' }}>
          <div style={{ maxWidth:920, margin:'0 auto', display:'flex', alignItems:'center', gap:10 }}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13" style={{ color:'#fbbf24', flexShrink:0 }}><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm1-4h-2V7h2v6z"/></svg>
            <span style={{ fontSize:12, color:'rgba(251,191,36,0.85)', fontWeight:500 }}>
              Vorschau-Modus — diese Seite zeigt Beispieldaten und ist nur für Admins sichtbar.
            </span>
          </div>
        </div>
      )}

      {/* Hero */}
      <div style={{ background:'linear-gradient(160deg,#0a1410 0%,#0f1a14 50%,#0c1611 100%)', borderBottom:'1px solid rgba(255,255,255,0.05)', padding:'48px 40px 44px', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-120, left:'30%', transform:'translateX(-50%)', width:700, height:550, background:'radial-gradient(ellipse,rgba(59,130,246,0.16) 0%,rgba(59,130,246,0.04) 50%,transparent 75%)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', top:-80, left:'72%', transform:'translateX(-50%)', width:600, height:480, background:'radial-gradient(ellipse,rgba(249,115,22,0.14) 0%,rgba(249,115,22,0.03) 50%,transparent 75%)', pointerEvents:'none' }}/>

        <div style={{ maxWidth:900, margin:'0 auto', position:'relative', display:'flex', alignItems:'center', gap:40 }}>

          {/* Left: text */}
          <div style={{ flex:1 }}>
            {report?.status === 'LIVE' && (
              <div className="pr-anim pr-d1" style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 14px', borderRadius:20, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.18)', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(255,255,255,0.92)', marginBottom:18 }}>
                <span style={{ width:6, height:6, borderRadius:'50%', background:'#ef4444', animation:'auf-pulse 2s ease-in-out infinite', display:'inline-block' }}/>
                Live
              </div>
            )}

            <h1 className="pr-anim pr-d1" style={{ fontSize:36, fontWeight:800, lineHeight:1.15, letterSpacing:'-0.02em', color:'rgba(225,240,232,0.97)', margin:'0 0 14px' }}>
              {report?.displayName || 'Campaign Report'}
            </h1>

            <p className="pr-anim pr-d2" style={{ fontSize:14, color:'rgba(225,240,232,0.88)', lineHeight:1.7, margin:'0 0 24px', maxWidth:400 }}>
              {t.desc}
            </p>

            <div className="pr-anim pr-d3" style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              {(() => {
                const badge = (icon, text) => (
                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:7, fontSize:12, fontWeight:500, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', color:'rgba(225,240,232,0.88)' }}>
                    {icon}{text}
                  </span>
                );
                const calIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
                const userIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
                return <>
                  {report?.companyName && badge(userIcon, report.companyName)}
                  {report?.period?.startsAt && badge(calIcon, `${t.started} ${new Date(report.period.startsAt).toLocaleDateString(lang === 'de' ? 'de-AT' : 'en-GB', { day:'2-digit', month:'short', year:'numeric' })}`)}
                  {badge(calIcon, report?.period?.endsAt ? `${t.ends} ${new Date(report.period.endsAt).toLocaleDateString(lang === 'de' ? 'de-AT' : 'en-GB', { day:'2-digit', month:'short', year:'numeric' })}` : t.running)}
                </>;
              })()}
            </div>

          </div>

          {/* Right: phone mockup */}
          <div className="auf-phone-wrap pr-anim pr-d2" style={{ flexShrink:0 }}>
            <div className="auf-phone-label">{t.preview}</div>
            <div className="auf-phone">
              <div className="auf-phone-frame">
                <div className="auf-phone-statusbar">
                  <span>9:41</span>
                  <span className="auf-phone-statusbar-icons">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><path d="M1.5 8.5a13 13 0 0 1 21 0M5 12a10 10 0 0 1 14 0M8.5 15.5a6 6 0 0 1 7 0M12 19h.01"/></svg>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><rect x="2" y="7" width="16" height="11" rx="2"/><path d="M22 11v3"/></svg>
                  </span>
                </div>
                <div className="auf-phone-ad">
                  {report?.advertisementImageDEUrl ? (
                    <img src={report.advertisementImageDEUrl} alt="Ad" className="auf-phone-ad-img" />
                  ) : (
                    <div className="auf-phone-ad-placeholder">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                      <span>Werbebild</span>
                    </div>
                  )}
                  <div className="auf-phone-overlay" />
                  <div className="auf-phone-ad-label">Anzeige</div>
                  <button className="auf-phone-close" type="button" tabIndex={-1}>✕</button>
                  <div className="auf-phone-content">
                    <div className="auf-phone-advertiser">{report?.displayName || ''}</div>
                    <div className="auf-phone-cta">
                      {t.learnMore}
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="9" height="9"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    </div>
                  </div>
                  <div className="auf-phone-home" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div style={{ maxWidth:860, margin:'0 auto', padding:'40px 20px 80px' }}>

        {loading && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, padding:'120px 0', color:'rgba(180,200,190,0.3)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" style={{ animation:'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
            <span style={{ fontSize:13, letterSpacing:'0.04em' }}>{t.loading}</span>
          </div>
        )}

        {error && (
          <div style={{ textAlign:'center', padding:'120px 0' }}>
            <div style={{ fontSize:14, color:'#f87171', marginBottom:10, fontWeight:600 }}>{error}</div>
            <div style={{ fontSize:12, color:'rgba(180,200,190,0.3)' }}>office@schick-vienna.at</div>
          </div>
        )}

        {report && <>
          {/* KPI Cards */}
          <div className="pr-anim pr-d3" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:24 }}>
            {METRIC_CARDS.map(m => (
              <div key={m.label} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:16, padding:'18px 18px 16px', position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, height:48, background:`linear-gradient(to bottom, ${m.accent}14, transparent)`, borderRadius:'16px 16px 0 0', pointerEvents:'none' }} />
                <div style={{ color: m.accent, marginBottom:10, opacity:0.9, lineHeight:0 }}>{m.icon}</div>
                <div style={{ fontSize:24, fontWeight:800, color:'rgba(225,240,232,0.97)', lineHeight:1, marginBottom:4, letterSpacing:'-0.02em' }}>{m.value}</div>
                <div style={{ fontSize:10, color:'rgba(180,200,190,0.3)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>{m.label}</div>
                <div style={{ fontSize:10, color: m.accent, opacity:0.8 }}>{m.sub}</div>
              </div>
            ))}
          </div>

          {/* Delivery */}
          <div className="pr-anim pr-d4" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:16, padding:'26px 28px', marginBottom:24 }}>
            <div style={{ marginBottom:18 }}>
              <span style={{ fontSize:9, color:'#2a3a2e', textTransform:'uppercase', letterSpacing:'0.12em', display:'block', marginBottom:10, fontWeight:600 }}>{t.delivery}</span>
              <span style={{ fontSize:40, fontWeight:800, color:'#f0f5f1', letterSpacing:'-0.04em', lineHeight:1, display:'block' }}>{delivPct}%</span>
            </div>
            <div style={{ position:'relative', height:4, background:'rgba(255,255,255,0.05)', borderRadius:99, overflow:'visible', marginBottom:14 }}>
              <div style={{ height:4, width:`${delivPct}%`, background:'rgba(255,255,255,0.7)', borderRadius:99, transition:'width 0.8s ease', position:'relative' }}>
                <div style={{ position:'absolute', right:-1, top:-4, width:12, height:12, borderRadius:'50%', background:'#f0f5f1', boxShadow:'0 0 0 3px #0f1410, 0 0 12px rgba(255,255,255,0.2)' }} />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:12, color:'rgba(200,221,208,0.8)', fontWeight:500 }}>{fmtNum(metrics?.impressions)} {t.delivered}</span>
              {metrics?.remainingImpressions != null && <span style={{ fontSize:12, color:'rgba(180,200,190,0.3)' }}>{fmtNum(metrics.remainingImpressions)} {t.remaining}</span>}
            </div>
          </div>

          {/* Chart */}
          <div className="pr-anim pr-d5" style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:16, padding:'22px 24px 18px', marginBottom:24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontSize:10, color:'rgba(180,200,190,0.3)', textTransform:'uppercase', letterSpacing:'0.12em', fontWeight:600 }}>{t.chart}</div>
              {dailyData.length > 0 && (
                <div style={{ display:'flex', gap:20 }}>
                  {[{k:'impressions',l:t.impressions,c:'#e8ede9'},{k:'clicks',l:t.clicks,c:'#00ff88'}].map(o => {
                    const active = chartMetric === o.k;
                    return <button key={o.k} onClick={() => setChartMetric(o.k)} style={{ background:'none', border:'none', cursor:'pointer', padding:'2px 0', fontSize:9, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', color: active ? o.c : '#1e2e20', borderBottom: active ? `1px solid ${o.c}` : '1px solid transparent', transition:'all 0.15s' }}>{o.l}</button>;
                  })}
                </div>
              )}
            </div>
            {dailyData.length > 0 ? (
              <>
                <BarChart data={dailyData} valueKey={chartMetric} secondKey={chartMetric === 'impressions' ? 'clicks' : undefined} secondColor="#00ff88" labelKey="label" color={chartMetric === 'clicks' ? '#00ff88' : '#e8ede9'} />
                {chartMetric === 'impressions' && (
                  <div style={{ display:'flex', gap:16, marginTop:14, justifyContent:'flex-end' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:10, color:'#2a3a2e', fontWeight:500 }}><div style={{ width:8, height:8, borderRadius:2, background:'#e8ede9', opacity:0.4 }}/> {t.impressions}</div>
                    <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:10, color:'#2a3a2e', fontWeight:500 }}><div style={{ width:8, height:8, borderRadius:2, background:'#00ff88' }}/> {t.clicks}</div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ height:140, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10 }}>
                <svg viewBox="0 0 48 32" width="48" height="32" style={{ opacity:0.12 }}>
                  <rect x="2"  y="20" width="8"  height="12" rx="1" fill="#e8ede9"/>
                  <rect x="13" y="14" width="8"  height="18" rx="1" fill="#e8ede9"/>
                  <rect x="24" y="8"  width="8"  height="24" rx="1" fill="#e8ede9"/>
                  <rect x="35" y="16" width="8"  height="16" rx="1" fill="#e8ede9"/>
                  <line x1="0" y1="31" x2="48" y2="31" stroke="#e8ede9" strokeWidth="0.5" opacity="0.5"/>
                </svg>
                <span style={{ fontSize:11, color:'rgba(180,200,190,0.2)', fontStyle:'italic' }}>
                  {t.emptyBreakdown}
                </span>
              </div>
            )}
          </div>

          {/* Breakdowns */}
          <div className="pr-anim pr-d6" style={{ marginBottom:24 }}>
            <div style={{ fontSize:9, color:'#2a3a2e', textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16, fontWeight:600 }}>{t.breakdown}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {BREAKDOWN_TABS.map(tab => {
                const rows = report.breakdowns ? (report.breakdowns[tab.key] || []) : [];
                const totalDel = rows.reduce((s, r) => s + (r.delivered || 0), 0) || 1;
                return (
                  <div key={tab.key} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:16, overflow:'hidden' }}>
                    <div style={{ padding:'14px 18px 13px', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ color:'#2a3a2e' }}>{tab.icon}</span>
                      <span style={{ fontSize:10, fontWeight:700, color:'#3a4e3e', textTransform:'uppercase', letterSpacing:'0.1em' }}>{tab.label}</span>
                    </div>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                      <thead><tr style={{ background:'rgba(0,0,0,0.2)' }}>{[t.segment,'Impr.',t.clicks,'CTR'].map((h,i) => <th key={h} style={{ padding:'9px 16px', textAlign: i===0 ? 'left' : 'right', color:'#1e2e20', fontWeight:700, fontSize:9, textTransform:'uppercase', letterSpacing:'0.08em' }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ padding:'20px 16px', textAlign:'center', color:'rgba(180,200,190,0.2)', fontSize:11, fontStyle:'italic' }}>
                              {t.emptyBreakdown}
                            </td>
                          </tr>
                        ) : rows.map((row, i) => {
                          const barW = Math.round(((row.delivered || 0) / totalDel) * 100);
                          return (
                            <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                              <td style={{ padding:'12px 16px' }}>
                                <div style={{ fontWeight:600, color:'#c8d5cb', fontSize:12, marginBottom:5 }}>{fmtKey(row.key)}</div>
                                <div style={{ height:2, background:'rgba(255,255,255,0.05)', borderRadius:99, width:64 }}><div style={{ height:2, width:`${barW}%`, background:'rgba(255,255,255,0.25)', borderRadius:99 }} /></div>
                              </td>
                              <td style={{ padding:'12px 16px', textAlign:'right', color:'rgba(180,200,190,0.35)', fontSize:12 }}>{fmtNum(row.impressions)}</td>
                              <td style={{ padding:'12px 16px', textAlign:'right', color:'rgba(180,200,190,0.35)', fontSize:12 }}>{fmtNum(row.clicks)}</td>
                              <td style={{ padding:'12px 16px', textAlign:'right', color:'#00ff88', fontWeight:700, fontSize:12 }}>{fmtPct(row.ctr)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </div>

        </>}
      </div>

      {/* ══ FOOTER ══ */}
      <footer className="pr-anim-fade pr-d7" style={{ background:'#111a15', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth:920, margin:'0 auto', padding:'44px 40px 40px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:40 }}>
            <div>
              <p style={{ margin:0, fontSize:12, color:'rgba(200,218,208,0.45)', lineHeight:1.7, maxWidth:320 }}>
                {t.pendingNote}
              </p>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontSize:10, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(200,218,208,0.3)', marginBottom:10 }}>
                {lang==='de' ? 'Kontakt' : 'Contact'}
              </div>
              <a href="mailto:office@schick-vienna.at" style={{ fontSize:12, color:'rgba(200,218,208,0.55)', textDecoration:'none', display:'block' }}>
                office@schick-vienna.at
              </a>
            </div>
          </div>
          <div style={{ marginTop:36, paddingTop:20, borderTop:'1px solid rgba(255,255,255,0.05)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:11, color:'rgba(200,218,208,0.25)' }}>
              © {new Date().getFullYear()} Schick Vienna
            </span>
            {report?.generatedAt && (
              <span style={{ fontSize:11, color:'rgba(200,218,208,0.25)' }}>
                {t.generatedAt} {new Date(report.generatedAt).toLocaleString(lang === 'de' ? 'de-AT' : 'en-GB', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
              </span>
            )}
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes auf-pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.5; transform:scale(0.8); } }
        @keyframes prFadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes prFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .pr-anim { animation: prFadeUp 0.55s cubic-bezier(0.22,1,0.36,1) both; }
        .pr-anim-fade { animation: prFadeIn 0.6s ease both; }
        .pr-d0  { animation-delay: 0ms; }
        .pr-d1  { animation-delay: 80ms; }
        .pr-d2  { animation-delay: 160ms; }
        .pr-d3  { animation-delay: 240ms; }
        .pr-d4  { animation-delay: 320ms; }
        .pr-d5  { animation-delay: 400ms; }
        .pr-d6  { animation-delay: 480ms; }
        .pr-d7  { animation-delay: 560ms; }
      `}</style>
    </div>
  );
}
